export interface AnchoredPopoverPosition {
  top: number;
  left: number;
}

export interface ComputeAnchoredPopoverOptions {
  /** Gap between anchor and popover in px. */
  gap?: number;
  /** Minimum distance from viewport edges in px. */
  padding?: number;
  /**
   * Prefer placing the popover above the anchor (composer bottom chrome).
   * Still flips below when there is not enough room above.
   */
  preferAbove?: boolean;
}

/**
 * Places a fixed popover near an anchor rect, flipping vertically and shifting
 * horizontally when the preferred placement would leave the viewport.
 */
export function computeAnchoredPopoverPosition(
  anchor: DOMRectReadOnly,
  popoverWidth: number,
  popoverHeight: number,
  options: ComputeAnchoredPopoverOptions = {}
): AnchoredPopoverPosition {
  const gap = options.gap ?? 6;
  const padding = options.padding ?? 8;
  const preferAbove = options.preferAbove ?? false;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;

  const below = anchor.bottom + gap;
  const above = anchor.top - gap - popoverHeight;

  let top: number;
  if (preferAbove) {
    if (above >= padding) {
      top = above;
    } else if (below + popoverHeight <= vh - padding) {
      top = below;
    } else {
      top = Math.max(padding, Math.min(below, vh - padding - popoverHeight));
    }
  } else if (below + popoverHeight > vh - padding) {
    if (above >= padding) {
      top = above;
    } else {
      top = Math.max(padding, Math.min(below, vh - padding - popoverHeight));
    }
  } else {
    top = below;
  }

  let left = anchor.left;
  if (left + popoverWidth > vw - padding) {
    left = vw - padding - popoverWidth;
  }
  if (left < padding) {
    left = padding;
  }

  return { top, left };
}
